import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DollarSign, Home, Wrench, Heart, Calendar, Award, Phone, MapPin } from "lucide-react";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";

export function DriverBenefits() {
  const { ref, isIntersecting } = useIntersectionObserver({ threshold: 0.1 });
  const benefits = [
    {
      icon: DollarSign,
      title: "Competitive Pay & Bonuses",
      points: [
        "Top industry pay rates",
        "Performance-based bonuses",
        "Consistent weekly payroll",
        "Paid orientation & training"
      ]
    },
    {
      icon: Home,
      title: "Home Time Guaranteed",
      points: [
        "Home weekly schedule",
        "Regional Texas routes",
        "Predictable time off",
        "Work-life balance priority"
      ]
    },
    {
      icon: Wrench,
      title: "Old School Fleet",
      points: [
        "Peterbilt Power Units: 1999 & Older (ELD Exempt)",
        "Freightliner Coronado Gliders available",
        "Well-maintained 48' & 53' flatbeds",
        "Clean, reliable equipment"
      ]
    },
    {
      icon: Heart,
      title: "Driver Perks",
      points: [
        "Performance bonuses",
        "Holiday stay at home",
        "1099 contractor status",
        "Flexible scheduling options"
      ]
    },
    {
      icon: Calendar,
      title: "Stable Work Schedule",
      points: [
        "Consistent freight lanes",
        "No forced dispatch",
        "Flexible scheduling options",
        "Respect for your time"
      ]
    },
    {
      icon: Award,
      title: "Professional Growth",
      points: [
        "Safety training programs",
        "Career advancement paths",
        "Recognition programs",
        "Family-owned values"
      ]
    }
  ];

  return (
    <section 
      id="careers" 
      ref={ref as React.RefObject<HTMLElement>}
      className={`py-20 md:py-32 bg-background transition-all duration-1000 ${
        isIntersecting ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2
            className="text-4xl md:text-5xl font-bold mb-6"
            style={{ fontFamily: "Space Grotesk, sans-serif" }}
            data-testid="text-benefits-title"
          >
            Why Drive for Ben Boaz Transport?
          </h2>
          <p className="text-xl text-muted-foreground leading-relaxed">
            Join a family-owned company that values its drivers. We offer more than just a job—
            we provide a career with stability, respect, and excellent compensation.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <Card
                key={index}
                className="p-6 hover-elevate active-elevate-2 transition-all duration-300"
                data-testid={`card-benefit-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-lg bg-accent flex items-center justify-center shrink-0">
                    <Icon className="h-6 w-6 text-accent-foreground" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                    <ul className="space-y-2">
                      {benefit.points.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <div className="h-1.5 w-1.5 rounded-full bg-accent mt-2 shrink-0" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Fuel Points CTA */}
        <div className="max-w-6xl mx-auto mb-16">
          <div className="relative bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-2xl overflow-hidden shadow-2xl text-white p-8 md:p-12">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute inset-0" style={{
                backgroundImage: 'radial-gradient(circle, white 15%, transparent 16%), radial-gradient(circle, white 15%, transparent 16%)',
                backgroundSize: '20px 20px',
                backgroundPosition: '0 0, 10px 10px'
              }} />
            </div>
            
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/60" />
            
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="flex-1">
                <div className="inline-block bg-accent text-accent-foreground text-xs font-bold px-3 py-1.5 rounded-lg mb-4 uppercase tracking-wide">
                  Driver Perks
                </div>
                <h3 
                  className="text-3xl md:text-5xl font-bold italic leading-tight mb-4"
                  style={{ fontFamily: "Space Grotesk, sans-serif" }}
                  data-testid="text-fuel-cta-title"
                >
                  WE BUY THE FUEL.<br />
                  <span className="text-accent">YOU RACK UP THE REWARDS.</span>
                </h3>
                <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                  We run the major networks—TA, Petro, Pilot Flying J, Love's—so you keep 100% of your points and shower credits. Tax-free value that adds up fast.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    asChild
                    size="lg"
                    className="bg-accent hover:bg-accent text-accent-foreground shadow-lg w-full sm:w-auto"
                  >
                    <a 
                      href="/careers#application"
                      data-testid="button-fuel-cta-apply"
                      className="flex items-center gap-2 justify-center"
                    >
                      Apply Now
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </a>
                  </Button>
                  <Button 
                    asChild
                    size="lg"
                    variant="outline"
                    className="bg-white/10 backdrop-blur-sm hover:bg-white/20 border-2 border-white/30 text-white w-full sm:w-auto"
                  >
                    <a 
                      href="tel:8327325501"
                      data-testid="link-fuel-cta-call"
                      className="flex items-center gap-2 justify-center"
                    >
                      Call (832) 732-5501
                    </a>
                  </Button>
                </div>
              </div>
              
              {/* Icon Graphic */}
              <div className="hidden md:block">
                <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 p-8 rounded-full">
                  <svg className="w-24 h-24 text-accent" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.77 7.23l.01-.01-3.72-3.72L15 4.56l2.11 2.11c-.94.36-1.61 1.26-1.61 2.33 0 1.38 1.12 2.5 2.5 2.5.36 0 .69-.08 1-.21v7.21c0 .55-.45 1-1 1s-1-.45-1-1V14c0-1.1-.9-2-2-2h-1V5c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v16h10v-7.5h1.5v5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V9c0-.69-.28-1.32-.73-1.77zM12 10H6V5h6v5zm6 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/>
                  </svg>
                </div>
              </div>
            </div>
            
            {/* Trust Badges */}
            <div className="relative z-10 mt-8 pt-8 border-t border-white/20">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-8 h-8 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <div className="text-sm font-bold">Shower Credits</div>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-8 h-8 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm font-bold">Points = Cash</div>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-8 h-8 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  <div className="text-sm font-bold">Rewards Cards</div>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-8 h-8 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  <div className="text-sm font-bold">Major Chains</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Requirements Section */}
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 md:p-12 bg-muted/30">
            <h3
              className="text-2xl md:text-3xl font-bold mb-6 text-center"
              style={{ fontFamily: "Space Grotesk, sans-serif" }}
            >
              Driver Requirements
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="h-6 w-6 rounded-full bg-accent flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-accent-foreground text-xs font-bold">✓</span>
                  </div>
                  <div>
                    <div className="font-semibold">Valid CDL Class A License</div>
                    <div className="text-sm text-muted-foreground">Current and in good standing</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-6 w-6 rounded-full bg-accent flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-accent-foreground text-xs font-bold">✓</span>
                  </div>
                  <div>
                    <div className="font-semibold">Minimum 1 Year Experience</div>
                    <div className="text-sm text-muted-foreground">Flatbed experience preferred</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-6 w-6 rounded-full bg-accent flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-accent-foreground text-xs font-bold">✓</span>
                  </div>
                  <div>
                    <div className="font-semibold">Clean Driving Record</div>
                    <div className="text-sm text-muted-foreground">Good safety history required</div>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="h-6 w-6 rounded-full bg-accent flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-accent-foreground text-xs font-bold">✓</span>
                  </div>
                  <div>
                    <div className="font-semibold">Pass DOT Physical</div>
                    <div className="text-sm text-muted-foreground">Meet all DOT requirements</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-6 w-6 rounded-full bg-accent flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-accent-foreground text-xs font-bold">✓</span>
                  </div>
                  <div>
                    <div className="font-semibold">Professional Attitude</div>
                    <div className="text-sm text-muted-foreground">Customer service focused</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-6 w-6 rounded-full bg-accent flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-accent-foreground text-xs font-bold">✓</span>
                  </div>
                  <div>
                    <div className="font-semibold">Able to Work Independently</div>
                    <div className="text-sm text-muted-foreground">Self-motivated and reliable</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-border">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <Phone className="h-8 w-8 text-accent" />
                  <div>
                    <div className="font-semibold">Questions About Driving for Us?</div>
                    <a
                      href="tel:8327325501"
                      className="text-accent hover:underline font-medium"
                      data-testid="link-phone-requirements"
                    >
                      Call (832) 732-5501
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <MapPin className="h-8 w-8 text-accent" />
                  <div>
                    <div className="font-semibold">Serving Regional Texas</div>
                    <div className="text-sm text-muted-foreground">Home weekly guarantee</div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}
